import React, { useMemo } from 'react'
import { useRef, useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { media } from '@rst/libs-ts'
import { isMobileOnly, isMobileSafari } from 'react-device-detect'
import {
	disableBodyScroll,
	clearAllBodyScrollLocks,
} from 'body-scroll-lock-upgrade'
import { Text } from '../../atoms/text'
import { color, RstIcon, typography } from '../../atoms'

type CloseProps = { close: VoidFunction; title: string }

type ModalContainerType = {
	mobile: boolean
	close: (...props: any) => any
	name: string
	noPadding: boolean
	children: React.ReactNode
	fixedHeader: boolean
	headerContent: string | React.ElementType<any>
	maxWidth: any
	maxHeight: any
	overflow: boolean
	className: any
	viewHeader: boolean
	CustomClose?: (props: CloseProps) => JSX.Element | null
	[key: string]: any
}

const ModalContainer = ({
	mobile,
	close,
	name,
	noPadding,
	children,
	fixedHeader,
	headerContent = null,
	maxWidth,
	maxHeight,
	overflow = true,
	className = '',
	viewHeader = true,
	customHeader,
	CustomClose,
}: ModalContainerType) => {
	const contentRef = useRef()
	const [update, forceUpdate] = useState(null)
	useEffect(() => {
		forceUpdate(1)

		// eslint-disable-next-line
		if (isMobileSafari) {
			document.body.style.position = 'absolute'
			document.body.style.maxWidth = '100vw'
		}
		return () => {
			// eslint-disable-next-line
			if (isMobileSafari) {
				document.body.style.position = 'static'
				document.body.style.maxWidth = undefined
			}
			clearAllBodyScrollLocks()
		}
	}, [])
	useEffect(() => {
		if (update) {
			const d = document.querySelector('.SCROLLLOCKTARGET')
			// @ts-ignore
			disableBodyScroll(d, { reserveScrollBarGap: true })
		}
	}, [update])

	const CloseElement = useMemo(
		() =>
			CustomClose ||
			(isMobileOnly || mobile ? MobileCloseElement : DesktopCloseElement),
		[CustomClose, mobile]
	)

	return (
		<Wrapper className={className} mobile={mobile} maxWidth={maxWidth}>
			{customHeader ? customHeader : null}
			{viewHeader && (
				<CloseElement close={close as VoidFunction} title={name} />
			)}

			<Content
				mobile={mobile}
				overflow={overflow}
				noPadding={noPadding}
				maxHeight={maxHeight}
				fixedHeader={fixedHeader}
				tabIndex="0"
				id="modal-content"
				className="scrollBlack SCROLLLOCKTARGET"
			>
				<input type="hidden" ref={contentRef} />
				{(!isMobileOnly || !mobile) && name && (
					<Header fixedHeader={fixedHeader}>
						<Text>{name}</Text>
						{headerContent}
					</Header>
				)}
				<Children mobile={mobile}>{children}</Children>
			</Content>
		</Wrapper>
	)
}

const DesktopCloseElement = ({ close }: CloseProps) => (
	<Close onClick={close}>
		<RstIcon type={'rst-close'} />
	</Close>
)

const MobileCloseElement = ({ close, title }: CloseProps) => {
	return (
		<MobileClose className={'DELETETARGETHEADERFULLSCREEN'} onClick={close}>
			<RstIcon type={'rst-arrows_left'} />
			<Text>{title}</Text>
		</MobileClose>
	)
}

const WrapperMobile = css`
	max-height: 100%;
	max-width: 100%;
	height: 100%;
`

const Wrapper = styled.div<{
	maxWidth?: string
	mobile?: boolean
}>`
	display: flex;
	position: relative;
	max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}vw` : '60vw')};
	min-width: 20vw;
	position: relative;

	@media (max-width: 922px) {
		max-width: 90vw;
		width: 100%;
		flex-direction: column;

		.scrollBlack::-webkit-scrollbar {
			width: 0;
		}
		.scrollBlack::-webkit-scrollbar-thumb {
			width: 0;
		}
	}

	${({ mobile }) =>
		mobile
			? WrapperMobile
			: css`
					${media.pure.less(media.size.xs)} {
						${WrapperMobile}
					}
			  `}
`

const contentMobileCss = css`
	min-height: 100%;
	max-height: none;
	border-radius: 0;
	padding: 16px;
	-webkit-backface-visibility: hidden;
	-moz-backface-visibility: hidden;
	backface-visibility: hidden;
	-webkit-overflow-scrolling: touch;
	max-width: 100vw;
`

const Content = styled.div<{
	noPadding?: boolean
	maxHeight?: string
	overflow?: boolean
	mobile?: boolean
	fixedHeader?: boolean
	tabIndex: any
}>`
	width: 100%;
	display: flex;
	flex-direction: column;
	background: ${color('gray-01')};
	${({ noPadding }) =>
		!noPadding &&
		css`
			padding: 20px;
		`}

	border-radius: 5px;
	max-height: ${({ maxHeight }) =>
		maxHeight ? `${maxHeight}vh` : 'calc(100vh - 100px)'};
	${({ overflow }) =>
		overflow &&
		css`
			overflow-y: auto;
		`}

	${({ mobile }) =>
		mobile
			? contentMobileCss
			: css`
					${media.pure.less(media.size.xs)} {
						${contentMobileCss}
					}
			  `}

	

	${({ fixedHeader }) =>
		fixedHeader &&
		css`
			padding: 70px 20px 20px;
		`}

	-webkit-user-select: text;
	user-select: text;
`

const Header = styled.div<{
	fixedHeader?: boolean
}>`
	width: 100%;
	display: flex;
	align-items: center;

	${Text} {
		font-size: 18px;
		font-weight: 500;
		margin-right: 10px;
	}

	${({ fixedHeader }) =>
		fixedHeader
			? css`
					padding-left: 20px;
					position: absolute;
					left: 0;
					z-index: 1;
					top: 0;
					width: calc(100% - 6px);
					height: 50px;
					background: ${color('gray-01')};
					box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.08);
			  `
			: css`
					margin-bottom: 20px;
			  `}
`

export const Close = styled.div`
	position: absolute;
	top: 5px;
	right: 10px;
	cursor: pointer;
	z-index: 3;
	width: 14px;
	height: 14px;
	padding: 1px;
	color: ${color('gray-20')};
`

const MobileClose = styled.div`
	max-width: 90vw;
	width: 100%;
	height: 50px;
	min-height: 50px;
	background: ${color('primary.main')};
	box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.2);
	color: ${color('gray-01')};
	padding: 0 20px;
	display: flex;
	align-items: center;

	${media.pure.less(media.size.xs)} {
		max-width: 100vw;
	}

	svg {
		margin-right: 30px;
	}

	${Text} {
		${typography.head4.bold};
		display: block;
		overflow: hidden;
		color: ${color('gray-01')};
		text-overflow: ellipsis;
		width: 75vw;
		white-space: nowrap;
	}

	&:-webkit-full-screen {
		display: none;
	}

	i {
		font-size: 24px;
	}
`

const Children = styled.div<{ mobile?: boolean }>`
	height: 100%;
`

ModalContainer.Wrapper = Wrapper
ModalContainer.Content = Content
ModalContainer.Header = Header
ModalContainer.MobileClose = MobileClose
ModalContainer.Close = Close
ModalContainer.Children = Children

export { ModalContainer }
